import React, { useState, useEffect, useContext } from 'react';
import { UserRoleContext } from '../context/UserRoleContext';
import { CreateUserRoleRequest, UpdateUserRoleRequest, UserRole, UserRoleHostOrg } from '../types/UserRole';
import { getRolesForOrganization } from '../services/organizationService';
import { getRolesForHost } from '../services/hostService';
import { createUserRole, deleteUserRole, getUserRoles, updateUserRole } from '../services/userRoleService';
import { createUser } from '../services/userService';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { ROLES } from '../constants/roles';
import { ROUTES } from '../constants/routes';

const RolesDashboard: React.FC<{}> = () => {

  const { userRole } = useContext(UserRoleContext);
  const [userRoles, setUserRoles] = useState<UserRoleHostOrg[]>([]);
  const [possibleUserRoles, setPossibleUserRoles] = useState<UserRole[]>([]);
  const [showPopupAdd, setShowPopupAdd] = useState<boolean>(false);
  const [showPopupRemove, setShowPopupRemove] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [roleToUpdate, setRoleToUpdate] = useState<UserRoleHostOrg | null>(null);
  const [roleToAdd, setRoleToAdd] = useState<CreateUserRoleRequest | null>();
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const { addToast } = useToast();

  const navigate = useNavigate();


  useEffect(() => {
    refreshRoleList();
  }, [userRole]);

  useEffect(() => {
    const fetchRoles = async () => {
      const roles = await getUserRoles();
      setPossibleUserRoles(roles);
    };
    fetchRoles();

  }, [userRole]);

  const refreshRoleList = () => {
    if (userRole?.organization_id) {
      const fetchOrgRoles = async () => {
        if (userRole?.organization_id) {
          const roles = await getRolesForOrganization(userRole?.organization_id);
          setUserRoles(roles.sort((a, b) => a.last_name.localeCompare(b.last_name)));
        };
        fetchOrgRoles();
      }
    } else if (userRole?.host_id) {
      const fetchHostRoles = async () => {
        if (userRole?.host_id) {
          const roles = await getRolesForHost(userRole?.host_id);
          setUserRoles(roles.sort((a, b) => a.last_name.localeCompare(b.last_name)));
        }
      };
      fetchHostRoles();
    }
  }

  const handleRemoveUser = () => {
    if (roleToUpdate) {
      deleteUserRole(roleToUpdate.user_role_id);
      setUserRoles((prev) =>
        prev.filter((user) => user.user_role_id !== roleToUpdate.user_role_id)
      );
      addToast(`User ${roleToUpdate.first_name} ${roleToUpdate.last_name} removed.`)
    } else {
      addToast('Error, user could not be deleted', true);
    }
    closePopupEdit();
  };

  const handleRoleChange = async (user_role_id: string, newRoleId: string) => {
    if (!user_role_id || !newRoleId) {
      return;
    }

    const updateRequest: UpdateUserRoleRequest = {
      user_role_id: user_role_id,
      role_id: newRoleId
    };
    try {

    const success = await updateUserRole(user_role_id, updateRequest);
    if (success) {
      addToast('Role updated');
    } else {
      addToast('Error updating role');
    }
    } catch (error) {
      addToast("Error updating role", true);
    }

    setUserRoles((prev) =>
      prev.map((user) =>
        user.user_role_id === user_role_id ? { ...user, role_id: newRoleId } : user
      )
    );
  };

  const handleEmailChange = (newEmail: string) => {
    setRoleToAdd((prevRole) => {
      if (!prevRole) return null;
      return { ...prevRole, email: newEmail };
    });
    setEmailValid(isValidEmail(newEmail));
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  const handleAddRoleChange = (role_id: string) => {
    setRoleToAdd((prevRole) => {
      if (!prevRole) return null;
      return { ...prevRole, role_id: role_id };
    });
  };



  const onCancel = (e: React.FormEvent) => {
    e.preventDefault();

    if (userRole?.organization_id) {
      navigate(ROUTES.ORGANIZATION_OVERVIEW);
    } else if (userRole?.host_id) {
      navigate(ROUTES.HOST_OVERVIEW);
    } else {
      navigate(ROUTES.HOST_ORG_OVERVIEW);
    }
  }

  const openPopup = () => {
    const newRole: CreateUserRoleRequest = {
      role_id: ROLES.ADMIN,
      organization_id: userRole?.organization_id || undefined,
      host_id: userRole?.host_id || undefined,
      email: ''
    };
    setRoleToAdd(newRole);
    setShowPopupAdd(true);
  }

  const addUser = () => {
    if (roleToAdd && emailValid) {
      console.log(roleToAdd);
      createUserRole(roleToAdd);
      refreshRoleList();
      closePopup();
      addToast('Request sent, if user does not have an account, try again later.');
    } else {
      addToast('Invalid email format', true);
    }
  }

  const closePopup = () => {
    setShowPopupAdd(false);
  }

  const closePopupEdit = () => {
    setRoleToUpdate(null);
    setShowPopupRemove(false);
  }

  const openPopupEdit = (user: UserRoleHostOrg) => {
    setRoleToUpdate(user);
    setShowPopupRemove(true);
  }



  return (
    <div><h2>User Roles</h2>
      <div className='card'>
        <button style={{ marginTop: '10px' }} onClick={openPopup}>
          Add New User to {userRole?.organization_id ? 'Organization' : 'Host'}
        </button>
        <div className='tableWrapper'>
          <table>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userRoles.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.first_name}</td>
                  <td>{user.last_name}</td>
                  <td>{user.email}</td>
                  <td>
                    <select
                      value={user.role_id}
                      onChange={(e) => handleRoleChange(user.user_role_id, e.target.value)}
                      style={{ width: '100%' }}
                      disabled={user.user_id == userRole?.user_id}
                    >
                      {possibleUserRoles.map((role) => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {user.user_id == userRole?.user_id ?
                      <button
                        className="actionButton"
                        disabled={true}
                      >
                        Self
                      </button>
                      : (<button
                        className="actionButton"
                        onClick={() => openPopupEdit(user)}
                      >
                        Remove
                      </button>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button style={{ marginLeft: '20px', marginTop: '10px' }} onClick={onCancel}>
          Back
        </button>
      </div>

      {showPopupAdd && (
        <div className="popOverContainer"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            padding: '20px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            zIndex: 1000,
            minWidth: '50%',
            maxHeight: '300px'
          }}>

          <h3>Add Existing Cyne Up User To A Role</h3>

          <div>
            <input type="email" placeholder='john.doe@aol.com' value={roleToAdd?.email && roleToAdd?.email}
              onChange={(e) => handleEmailChange(e.target.value)} />
            <select
              value={roleToAdd?.role_id}
              onChange={(e) => handleAddRoleChange(e.target.value)}
            >
              {possibleUserRoles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </div>
          {(() => {
            const selectedRole = possibleUserRoles.find(
              (r) => r.role_id === roleToAdd?.role_id
            );
            return selectedRole?.role_description ? (
              <label>
                <i>{selectedRole.role_description}</i>
              </label>
            ) : null;
          })()}          <div className="buttonsRow">
            {/* Cancel Button */}
            <div className='buttonsLeft'>
              <button
                style={{
                  fontSize: '16px',
                  backgroundColor: '#888888',
                }}
                onClick={closePopup}
              >
                Close
              </button>

            </div>
            <div className='buttonsRight'>
              <button onClick={() => addUser()} disabled={!emailValid}>Add</button>
            </div>

          </div>
          {message && <div className="toast-message">{message}</div>}
        </div>)}



      {showPopupRemove && (
        <div className="popOverContainer"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            padding: '20px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            zIndex: 1000,
            minWidth: '200px',
            maxHeight: '300px'
          }}>

          <h3>Are you sure you want to remove {roleToUpdate?.first_name} {roleToUpdate?.last_name} - {roleToUpdate?.role_name}?</h3>
          <h4>This does not delete their account or their work.</h4>


          <div className="buttonsRow">
            <div className='buttonsLeft'>
              <button
                className='cancelButton'
                onClick={closePopupEdit}
              >
                Close
              </button>
            </div>
            <div className='buttonsRight'>
              <button className='warningButton' onClick={() => handleRemoveUser()}>Remove</button>

            </div>


          </div>
          {message && <div className="toast-message">{message}</div>}
        </div>)}

      {(showPopupAdd || showPopupRemove) && (
        <div className="popOverBackdrop"></div>

      )}
    </div>
  );
};

export default RolesDashboard;
